import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Briefcase,
  CreditCard,
  HandCoins,
  Landmark,
  Building2,
  Calculator,
  Scale,
  // Megaphone, // Comercial (oculto)
  ShieldAlert,
  LineChart,
  Settings,
  Bell,
} from 'lucide-react';
import { FEATURES } from './features';

export type PermissionAction = 'read' | 'write' | 'critical';

/** @deprecated flat item — kept for PermissionAction import compatibility */
export interface NavItem {
  name: string;
  href: string;
  icon: string;
  module: string;
  action?: PermissionAction;
}

export interface NavLeaf {
  id: string;
  label: string;
  href: string;
  module: string;
  action?: PermissionAction;
  /** Subtítulo visual dentro del grupo (Cartera, Gestión comercial, …) */
  section?: string;
}

export type NavEntry =
  | {
      id: string;
      type: 'link';
      label: string;
      href: string;
      icon: LucideIcon;
      module: string;
      action?: PermissionAction;
    }
  | {
      id: string;
      type: 'group';
      label: string;
      icon: LucideIcon;
      children: NavLeaf[];
    }
  | { id: string; type: 'separator' };

export const NAVIGATION_TREE: NavEntry[] = [
  {
    id: 'dashboard',
    type: 'link',
    label: 'Tablero',
    href: '/',
    icon: LayoutDashboard,
    module: 'dashboard',
    action: 'read',
  },
  /**
   * Accesos del día a día. Mismos módulos de permiso que antes
   * (customers / applications / loans) — no se crean features nuevas.
   */
  {
    id: 'operations',
    type: 'group',
    label: 'Operativo',
    icon: Briefcase,
    children: [
      {
        id: 'ops-customers',
        label: 'Listado clientes',
        href: '/customers',
        module: 'customers',
        action: 'read',
      },
      {
        id: 'ops-applications',
        label: 'Presolicitudes pendientes',
        href: '/applications',
        module: 'applications',
        action: 'read',
      },
      {
        id: 'ops-loans',
        label: 'Créditos',
        href: '/loans',
        module: 'loans',
        action: 'read',
      },
      {
        id: 'ops-pending',
        label: 'Pendientes de desembolso',
        href: '/loans/pending',
        module: 'loans-pending',
        action: 'read',
      },
      {
        id: 'ops-transfer-pending',
        label: 'Pendientes de transferencia',
        href: '/loans/transfer-pending',
        module: 'loans-transfer-pending',
        action: 'read',
      },
      {
        id: 'ops-compliance',
        label: 'Bloqueos y buró',
        href: '/compliance',
        module: 'compliance',
        action: 'read',
      },
      ...(FEATURES.kyc
        ? [
            {
              id: 'ops-kyc',
              label: 'KYC',
              href: '/kyc',
              module: 'kyc',
              action: 'read' as const,
            },
          ]
        : []),
    ],
  },
  {
    id: 'credits',
    type: 'group',
    label: 'Créditos',
    icon: CreditCard,
    children: [
      { id: 'credits-renewals', label: 'Renovaciones', href: '/loans/renewals', module: 'loans-renewals', action: 'read', section: 'Cartera' },
      { id: 'credits-plans', label: 'Planes', href: '/products', module: 'loan-products', action: 'read', section: 'Cartera' },
      { id: 'credits-objectives', label: 'Objetivos', href: '/objectives', module: 'objectives', action: 'read', section: 'Gestión comercial' },
    ],
  },
  {
    id: 'collections',
    type: 'group',
    label: 'Cobranzas',
    icon: HandCoins,
    children: [
      { id: 'col-workstation', label: 'Cobranzas', href: '/collections', module: 'collections-desk', action: 'read' },
      { id: 'col-payments', label: 'Pagos', href: '/payments', module: 'payments', action: 'read' },
      { id: 'col-payroll', label: 'Archivo de descuentos', href: '/payroll-discounts', module: 'payroll-discounts', action: 'read' },
      { id: 'col-payroll-bank', label: 'Cobros pendientes entidades', href: '/payroll-batch-collections', module: 'payroll-batch', action: 'read' },
      { id: 'col-bapro', label: 'Débito Provincia (BAPRO)', href: '/bapro-debit', module: 'payments', action: 'read' },
    ],
  },
  {
    id: 'treasury',
    type: 'group',
    label: 'Tesorería',
    icon: Landmark,
    children: [
      { id: 'tre-position', label: 'Posición de tesorería', href: '/treasury/position', module: 'treasury-position', action: 'write' },
      { id: 'tre-move', label: 'Mover fondos', href: '/treasury/move', module: 'treasury-move', action: 'write' },
      { id: 'tre-transfers', label: 'Movimientos de fondos', href: '/treasury/transfers', module: 'treasury-transfers', action: 'write' },
      { id: 'tre-banks', label: 'Bancos', href: '/treasury/banks', module: 'treasury-banks', action: 'write' },
      { id: 'tre-supervision', label: 'Supervisión', href: '/treasury/supervision', module: 'treasury-supervision', action: 'write' },
      { id: 'tre-cash', label: 'Mi caja', href: '/cash', module: 'cash-mine', action: 'read' },
      { id: 'tre-registers', label: 'Cajas', href: '/treasury/cash-registers', module: 'cash-registers', action: 'write' },
      { id: 'tre-approvals', label: 'Aprobaciones', href: '/treasury/approvals', module: 'treasury-approvals', action: 'write' },
      { id: 'tre-config', label: 'Configuración', href: '/treasury/config', module: 'treasury-config', action: 'write' },
    ],
  },
  {
    id: 'banks',
    type: 'group',
    label: 'Bancos',
    icon: Building2,
    children: [
      { id: 'banks-recon', label: 'Conciliación bancaria', href: '/bank-reconciliation', module: 'bank-reconciliation', action: 'read' },
    ],
  },
  {
    id: 'payables',
    type: 'group',
    label: 'Proveedores',
    icon: HandCoins,
    children: [
      { id: 'pay-ap', label: 'Proveedores y gastos', href: '/payables', module: 'payables', action: 'read' },
    ],
  },
  {
    id: 'accounting',
    type: 'group',
    label: 'Contabilidad',
    icon: Calculator,
    children: [
      { id: 'acc-home', label: 'Inicio', href: '/accounting', module: 'accounting', action: 'read' },
      { id: 'acc-diario', label: 'Diario', href: '/accounting/books/journal', module: 'accounting-journal', action: 'read', section: 'Libros' },
      { id: 'acc-mayor', label: 'Mayor', href: '/accounting/books/ledger', module: 'accounting-ledger', action: 'read', section: 'Libros' },
      { id: 'acc-sumas', label: 'Sumas y saldos', href: '/accounting/books/trial', module: 'accounting-trial', action: 'read', section: 'Libros' },
      { id: 'acc-resultados', label: 'Estado de resultados', href: '/accounting/statements/income', module: 'accounting-income', action: 'read', section: 'Estados financieros' },
      { id: 'acc-patrimonial', label: 'Estado patrimonial', href: '/accounting/statements/balance', module: 'accounting-balance', action: 'read', section: 'Estados financieros' },
      { id: 'acc-flujo', label: 'Flujo de fondos', href: '/accounting/statements/cashflow', module: 'accounting-cashflow', action: 'read', section: 'Estados financieros' },
      { id: 'acc-periodos', label: 'Cierre mensual', href: '/accounting/close', module: 'accounting-close', action: 'write', section: 'Cierre' },
      { id: 'acc-periods-hist', label: 'Períodos históricos', href: '/accounting/close/periods', module: 'accounting-periods', action: 'read', section: 'Cierre' },
      { id: 'acc-fy', label: 'Ejercicios', href: '/accounting/close/fiscal-years', module: 'accounting-fy', action: 'write', section: 'Cierre' },
      { id: 'acc-wp', label: 'Papeles de trabajo', href: '/accounting/close/working-papers', module: 'accounting-wp', action: 'read', section: 'Cierre' },
      { id: 'acc-chart', label: 'Plan de cuentas', href: '/accounting/masters/chart', module: 'accounting-chart', action: 'write', section: 'Configuración' },
      { id: 'acc-cc', label: 'Centros de costo', href: '/accounting/masters/cost-centers', module: 'accounting-cc', action: 'write', section: 'Configuración' },
      { id: 'acc-bu', label: 'Unidades de negocio', href: '/accounting/masters/business-units', module: 'accounting-bu', action: 'write', section: 'Configuración' },
      { id: 'acc-map', label: 'Mapeos contables', href: '/accounting/masters/mappings', module: 'accounting-map', action: 'write', section: 'Configuración' },
      { id: 'acc-imports', label: 'Importaciones', href: '/accounting/tools/imports', module: 'accounting-imports', action: 'write', section: 'Herramientas' },
      { id: 'acc-inflation', label: 'Ajuste por inflación', href: '/accounting/tools/inflation', module: 'accounting-inflation', action: 'write', section: 'Herramientas' },
      { id: 'acc-fx', label: 'Multimoneda', href: '/accounting/tools/fx', module: 'accounting-fx', action: 'write', section: 'Herramientas' },
    ],
  },
  {
    id: 'fiscal',
    type: 'group',
    label: 'Fiscal / IVA',
    icon: Scale,
    children: [
      { id: 'fis-home', label: 'Fiscal y legal', href: '/fiscal', module: 'fiscal', action: 'read' },
    ],
  },
  {
    id: 'risk',
    type: 'group',
    label: 'Riesgo y Control',
    icon: ShieldAlert,
    children: [
      { id: 'risk-mgmt', label: 'Gestión de riesgo', href: '/risk', module: 'risk', action: 'read' },
      { id: 'risk-audit', label: 'Auditoría', href: '/audit', module: 'audit', action: 'read' },
    ],
  },
  {
    id: 'analytics',
    type: 'group',
    label: 'Análisis y Reportes',
    icon: LineChart,
    children: [
      { id: 'an-analytics', label: 'Analytics', href: '/analytics', module: 'analytics', action: 'read' },
      { id: 'an-reports', label: 'Reportes', href: '/reports', module: 'reports', action: 'read' },
      { id: 'an-ops', label: 'Reportes operativos', href: '/reports/operational', module: 'reports-operational', action: 'read' },
      { id: 'an-legacy', label: 'Reportes legacy', href: '/reports/legacy', module: 'reports-legacy', action: 'read' },
    ],
  },
  { id: 'sep-admin', type: 'separator' },
  {
    id: 'admin',
    type: 'group',
    label: 'Administración',
    icon: Settings,
    children: [
      { id: 'adm-branches', label: 'Sucursales y Cajas', href: '/branches', module: 'branches', action: 'read' },
      { id: 'adm-users', label: 'Usuarios', href: '/users', module: 'users', action: 'read' },
      { id: 'adm-settings', label: 'Configuración', href: '/settings', module: 'settings', action: 'read' },
      { id: 'adm-masters', label: 'Datos maestros', href: '/reference-data', module: 'reference-data', action: 'read' },
    ],
  },
  {
    id: 'notifications',
    type: 'link',
    label: 'Notificaciones',
    href: '/notifications',
    icon: Bell,
    module: 'notifications',
    action: 'read',
  },
];

/** Lista plana de hojas/links con ruta (para permisos y utilidades). */
export function flattenNavigation(tree: NavEntry[] = NAVIGATION_TREE): Array<{
  href: string;
  module: string;
  action?: PermissionAction;
  label: string;
}> {
  const out: Array<{ href: string; module: string; action?: PermissionAction; label: string }> = [];
  for (const entry of tree) {
    if (entry.type === 'link') {
      out.push({ href: entry.href, module: entry.module, action: entry.action, label: entry.label });
    } else if (entry.type === 'group') {
      for (const child of entry.children) {
        out.push({ href: child.href, module: child.module, action: child.action, label: child.label });
      }
    }
  }
  return out;
}

/** @deprecated Prefer NAVIGATION_TREE — alias plano para compatibilidad. */
export const NAVIGATION: NavItem[] = flattenNavigation().map((item) => ({
  name: item.label,
  href: item.href,
  icon: '',
  module: item.module,
  action: item.action,
}));

export const ROUTE_PERMISSIONS: Record<string, { module: string; action?: PermissionAction }> =
  Object.fromEntries(
    flattenNavigation().map((item) => [item.href, { module: item.module, action: item.action }]),
  );

ROUTE_PERMISSIONS['/customers/:id'] = { module: 'customers', action: 'read' };
ROUTE_PERMISSIONS['/applications/new'] = { module: 'loans', action: 'read' };
ROUTE_PERMISSIONS['/loans/pending'] = { module: 'loans', action: 'read' };

export function isNavHrefActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  if (pathname === href) return true;
  if (!pathname.startsWith(`${href}/`)) return false;
  // Si existe otra ruta del menú más específica que también coincide, no marcar el padre.
  const moreSpecific = flattenNavigation().some(
    (leaf) =>
      leaf.href !== href &&
      leaf.href.startsWith(`${href}/`) &&
      (pathname === leaf.href || pathname.startsWith(`${leaf.href}/`)),
  );
  return !moreSpecific;
}
